<template>
  <v-container fluid>
    <!-- TITLE: PROJECT NAME + DELETE BTN -->
    <v-row>
      <v-col
        class="d-flex align-center"
        sm="8"
        md="7"
        xl="8"
        @dblclick="view"
      >
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div
              class="body-1 text-truncate"
              v-on="on"
            >
              {{ project.replace(/.zip$/, "") }}
            </div>
          </template>
          <span> <v-icon dense>mdi-file-document-outline</v-icon>{{ " " + project.replace(/.zip$/, "") }} </span>
          <br />
          <span v-if="showPath()">
            <v-icon>mdi-folder-marker-outline</v-icon>{{ " " + path.replace(/.zip$/, "") }}
          </span>
        </v-tooltip>
      </v-col>
      <v-col
        sm="4"
        md="5"
        xl="4"
        class="d-flex justify-end"
      >
        <dialog-repair-not-found
          v-if="notFound && !repaired"
          :schedule="schedule"
          :project-id-to-repair="projectId"
          :project-name="project"
          icon="mdi-alert"
          :get-size="23"
          :no-tool-tip="false"
          :title-tool-tip="$t('schedule.projectNotFound')"
          tool-name="text"
          @schedule-repaired="repaired = true"
        />
        <v-tooltip
          v-if="playing"
          top
        >
          <template v-slot:activator="{ on }">
            <v-icon
              color="success"
              class="mr-1"
              v-on="on"
            >
              mdi-motion-play-outline
            </v-icon>
          </template>
          <span>{{ $t("schedule.active") }}</span>
        </v-tooltip>
        <dialog-duration-schedule-edit
          :schedule="schedule"
          icon="mdi-timer-play-outline"
          :get-size="23"
          :no-tool-tip="false"
          tool-name="text"
        />
        <button-dialog-delete
          v-if="!newSchedule"
          color="warning"
          :icon-mdi="deleteIcn"
          :icon-only="true"
          :no-text="true"
          :text="$t('popup.schedule')"
          @ok-pushed="removeSchedule()"
        />
      </v-col>
    </v-row>
    <!-- ROW: DATE PICKER -->
    <v-row
      class="mt-4"
      dense
    >
      <v-col>
        <v-menu
          v-model="pickerDtFrom"
          :disabled="permanent"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateFrom"
              :disabled="permanent"
              :label="$t('schedule.date-from')"
              prepend-icon="event"
              class="caption"
              readonly
              clearable
              dense
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateFrom"
            first-day-of-week="1"
            no-title
            scrollable
            locale="fr-fr"
            :min="dateFromMin"
            :max="dateFromMax"
            @input="pickerDtFrom = false"
          />
        </v-menu>
      </v-col>
      <v-col>
        <v-menu
          v-model="pickerDtTo"
          :disabled="permanent"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="dateTo"
              :disabled="permanent"
              :label="$t('schedule.date-to')"
              prepend-icon="event"
              class="caption"
              readonly
              clearable
              dense
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="dateTo"
            first-day-of-week="1"
            no-title
            scrollable
            locale="fr-fr"
            :min="dateToMin"
            @input="pickerDtTo = false"
          />
        </v-menu>
      </v-col>
    </v-row>
    <!-- ROW: TIME PICKER -->
    <v-row dense>
      <v-col>
        <v-menu
          ref="pickerTFrom"
          v-model="pickerTFrom"
          :close-on-content-click="false"
          :return-value.sync="timeFrom"
          :disabled="permanent"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="timeFrom"
              :disabled="permanent"
              :label="$t('schedule.time-from')"
              prepend-icon="access_time"
              class="caption"
              readonly
              clearable
              dense
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-time-picker
            v-if="pickerTFrom"
            v-model="timeFrom"
            format="24hr"
            scrollable
            @click:minute="$refs.pickerTFrom.save(timeFrom)"
          />
        </v-menu>
      </v-col>
      <v-col>
        <v-menu
          ref="pickerTTo"
          v-model="pickerTTo"
          :close-on-content-click="false"
          :return-value.sync="timeTo"
          :disabled="permanent"
          offset-y
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="timeTo"
              :disabled="permanent"
              :label="$t('schedule.time-to')"
              prepend-icon="access_time"
              class="caption"
              readonly
              clearable
              dense
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-time-picker
            v-if="pickerTTo"
            v-model="timeTo"
            format="24hr"
            scrollable
            @click:minute="$refs.pickerTTo.save(timeTo)"
          />
        </v-menu>
      </v-col>
    </v-row>
    <!-- ROW: DAYS LIST -->
    <v-row dense>
      <v-col class="text-center">
        <v-chip
          v-for="day in days"
          :key="day.day"
          class="ma-1"
          :color="showDayColor(day)"
          :disabled="permanent"
          @click="day.active = !day.active"
        >
          {{ $t("calendar.days-abridged." + day.day) }}
        </v-chip>
      </v-col>
    </v-row>
    <!-- ROW: PERMANENT SWITCH -->
    <v-row dense>
      <v-col class="text-center">
        <v-switch
          v-model="permanent"
          :label="$t('schedule.permanent')"
          class="mt-1 mb-0"
          dense
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
// Components
import buttonDialogDelete from "./ButtonDialogDelete";
import dialogDurationScheduleEdit from "../Dialog/DialogDurationScheduleEdit";
import dialogRepairNotFound from "../Dialog/DialogRepairNotFound";

// Connectors
import { ScheduleConnector } from "../Database/ScheduleConnector";

// Libs
const moment = require("moment");

// Const
const currentDate = new Date();

export default {
  components: {
    buttonDialogDelete,
    dialogDurationScheduleEdit,
    dialogRepairNotFound,
  },
  props: {
    whileTime: {
      type: Number,
      default: 0,
    },
    project: {
      type: String,
      default: "",
    },
    projectId: {
      type: Number,
      default: null,
    },
    path: {
      type: String,
      default: "",
    },
    schedule: {
      type: Object,
      required: true,
    },
    newSchedule: {
      type: Boolean,
      default: false,
    },
    notFound: {
      type: Boolean,
      default: false,
    },
    disableValidation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scheduleConnector: new ScheduleConnector(this.$store.state.user),
      deleteIcn: "delete",
      pickerDtFrom: false,
      pickerDtTo: false,
      pickerTFrom: false,
      pickerTTo: false,
      openDialogDuration: false,
      openDialogRepair: false,
      repaired: false,
    };
  },
  computed: {
    dateFrom: {
      get() {
        return this.schedule.dateFrom;
      },
      set(val) {
        this.schedule.dateFrom = val;
        this.checkPermanent();
      },
    },
    dateFromMax() {
      if (this.dateTo != null) {
        return this.dateTo;
      }
      return "";
    },
    dateFromMin() {
      return currentDate.toISOString();
    },
    dateTo: {
      get() {
        return this.schedule.dateTo;
      },
      set(val) {
        this.schedule.dateTo = val;
        this.checkPermanent();
      },
    },
    dateToMin() {
      if (this.dateFrom == null) {
        return this.dateFromMin;
      }
      return this.dateFrom;
    },
    days: {
      get() {
        return this.schedule.days;
      },
      set(val) {
        this.schedule.days = val;
      },
    },
    permanent: {
      get() {
        return this.schedule.permanent;
      },
      set(val) {
        this.schedule.permanent = val;
      },
    },
    playing: {
      get() {
        if (this.schedule.permanent) {
          return true;
        } else {
          let dateFrom = this.schedule.dateFrom;
          let dateTo = this.schedule.dateTo;

          if (dateFrom == null) {
            dateFrom = "2000-01-01";
          }

          if (dateTo == null) {
            dateTo = "2099-12-31";
          }

          // Check if today is between dateFrom / dateTo
          if (moment().isBetween(dateFrom, dateTo)) {
            const currentTime = moment().format("HH:mm");
            const currentDay = moment().isoWeekday() - 1;
            let timeFrom = this.schedule.timeFrom;
            let timeTo = this.schedule.timeTo;

            // If the two times are defined
            if (timeFrom != null && timeTo != null) {
              if (timeFrom <= timeTo) {
                // If timeFrom <= timeTo check if the project is between these two values
                if (currentTime < timeFrom || currentTime > timeTo) {
                  return false;
                }
              } else {
                // If timeFrom > timeTo check if the project is beyond one of these two values
                if (currentTime > timeTo && currentTime < timeFrom) {
                  return false;
                }
              }
            }
            // If timeFrom is defined and timeTo not we check if we are after timeFrom
            if (timeFrom != null && timeTo == null) {
              if (currentTime < timeFrom) {
                return false;
              }
            }
            // If timeTo is defined and timeFrom not we check if we are before timeTo
            if (timeFrom == null && timeTo != null) {
              if (currentTime > timeTo) {
                return false;
              }
            }

            if (this.schedule.days[currentDay] != null) {
              if (this.schedule.days[currentDay].active == 0) {
                return false;
              }
            }

            return true;
          } else {
            return false;
          }
        }
      },
    },
    timeFrom: {
      get() {
        return this.schedule.timeFrom;
      },
      set(val) {
        this.schedule.timeFrom = val;
        this.checkPermanent();
      },
    },
    timeTo: {
      get() {
        return this.schedule.timeTo;
      },
      set(val) {
        this.schedule.timeTo = val;
        this.checkPermanent();
      },
    },
  },
  methods: {
    checkPermanent() {
      // TODO: check also with days modification... the computed setter not working when days modified
      if (
        this.dateFrom == null &&
        this.dateTo == null &&
        this.timeFrom == null &&
        this.timeTo == null &&
        this.days[0].active &&
        this.days[1].active &&
        this.days[2].active &&
        this.days[3].active &&
        this.days[4].active &&
        this.days[5].active &&
        this.days[6].active
      ) {
        this.permanent = true;
      }
    },
    dateNotTheSame() {
      if (this.dateFrom == this.dateTo && this.dateFrom != null && this.dateTo != null) {
        return false;
      }
      return true;
    },
    showDayColor(day) {
      if (day.active) {
        return "primary";
      }
      return "";
    },
    removeSchedule() {
      this.scheduleConnector.deleteScheduleForPlayer(this.schedule.projectData.group).then(() => {
        this.$emit("refresh");
      });
    },
    isValid() {
      if (new Date(this.schedule.dtTo) < new Date(this.schedule.dtFrom) && !this.schedule.permanent) {
        this.$emit("update:disableValidation", true);
        return;
      }
      this.$emit("update:disableValidation", false);
      return;
    },
    view() {
      // Set root path
      let projectRoot = this.$store.state.config.dirs.mediacenter + this.$store.state.user.socId;
      projectRoot = projectRoot.replace(this.$store.state.config.dirs.app, "");

      const projectData = {
        projId: this.schedule.projectData.id_proj,
        projectName: this.schedule.projectData.name,
        savePath: projectRoot + this.schedule.projectData.path,
      };

      this.$store.dispatch("open", projectData).project;
      this.$router.push({ path: "/project/1" });
    },
    showPath() {
      const nb = this.path.split("/");
      if (nb.length == 2) {
        return false;
      } else {
        return true;
      }
    },
    showDialogDurationScheduleEdit() {
      this.openDialogDuration = true;
    },
    closeDialogDurationScheduleEdit() {
      this.openDialogDuration = false;
    },
    showDialogRepairNotFound() {
      this.openDialogRepair = true;
    },
  },
};
</script>

<style>
.title-offset {
  position: relative;
  top: 6px;
  left: 5px;
}
</style>
